import { BillingCycle, WorkspacePlan } from '@up2stay/up2promo-api-client';
import { Button, cn } from '@up2stay/up2promo-ui';
import { Check, Lock } from 'lucide-react';

import {
  formatEurAmount,
  yearlyMonthlyEquivalent,
} from '@/core/billing/format-eur';
import { PLAN_PRICING, TRIAL_DAYS } from '@/core/billing/plan-catalog';
import { BillingCycleToggle } from '@/core/components/billing/billing-cycle-toggle';
import { BillingFeatureComparison } from '@/core/components/billing/billing-feature-comparison';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';

export interface PlanPickerProps {
  selectedPlan: WorkspacePlan | null;
  onPlanChange: (plan: WorkspacePlan) => void;
  billingCycle: BillingCycle;
  onBillingCycleChange: (cycle: BillingCycle) => void;
  currentPlan?: WorkspacePlan | null;
  promoteTrial?: boolean;
  isMobile: boolean;
  canManage: boolean;
  ctaBusy?: boolean;
  onSelectPlanAction: (plan: WorkspacePlan) => void;
  onContactEnterprise: () => void;
  /** Hide comparison table (e.g. when embedded elsewhere). */
  hideComparison?: boolean;
  /** Plan marked „Empfohlen“ (default Plus). */
  recommendedPlan?: WorkspacePlan;
  /**
   * Locked feature that opened the page (DEV-575): listed first on the
   * recommended card, which is then highlighted even without a trial.
   */
  focusFeatureLabel?: string | null;
  className?: string;
}

type PlanKey =
  | WorkspacePlan.FREE
  | WorkspacePlan.PLUS
  | WorkspacePlan.TEAMS
  | WorkspacePlan.ENTERPRISE;

export function PlanPicker({
  selectedPlan,
  onPlanChange,
  billingCycle,
  onBillingCycleChange,
  currentPlan,
  promoteTrial = false,
  isMobile,
  canManage,
  ctaBusy,
  onSelectPlanAction,
  onContactEnterprise,
  hideComparison,
  recommendedPlan = WorkspacePlan.PLUS,
  focusFeatureLabel,
  className,
}: PlanPickerProps) {
  const dict = useDictionary();
  const plansPage = dict.workspace.billing.plansPage;
  const locale = useLocale();
  const days = String(TRIAL_DAYS);
  const isYearly = billingCycle === BillingCycle.YEARLY;

  const ordered: PlanKey[] = isMobile
    ? [WorkspacePlan.PLUS, WorkspacePlan.TEAMS, WorkspacePlan.ENTERPRISE]
    : [
        WorkspacePlan.FREE,
        WorkspacePlan.PLUS,
        WorkspacePlan.TEAMS,
        WorkspacePlan.ENTERPRISE,
      ];

  return (
    <div className={cn('@container/plans flex flex-col gap-7', className)}>
      <div
        className={cn(
          'flex gap-6',
          isMobile ? 'flex-col' : 'flex-row items-end justify-between gap-6',
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <h2
            className={cn(
              'm-0 font-semibold tracking-[-0.02em] text-foreground',
              isMobile
                ? 'text-[22px] leading-tight'
                : 'text-[28px] leading-tight',
            )}
          >
            {plansPage.heroTitle.replace('{days}', days)}
          </h2>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-muted-foreground">
            {[
              plansPage.benefitTrial.replace('{days}', days),
              plansPage.benefitCard,
              plansPage.benefitCancel,
            ].map((benefit) => (
              <span key={benefit} className="inline-flex items-center gap-1.5">
                <Check className="size-4 shrink-0 text-primary" aria-hidden />
                {benefit}
              </span>
            ))}
          </div>
        </div>
        <BillingCycleToggle
          billingCycle={billingCycle}
          onBillingCycleChange={onBillingCycleChange}
        />
      </div>

      {isMobile && currentPlan === WorkspacePlan.FREE ? (
        <div className="flex items-center gap-3 rounded-[18px] border border-border bg-card px-4 py-3.5">
          <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
            {plansPage.currentBadge}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-semibold text-foreground">
              {plansPage.currentFreeTitle}
            </span>
            <span className="text-xs text-muted-foreground">
              {plansPage.currentFreeDetail}
            </span>
          </span>
        </div>
      ) : null}

      <div
        className={cn(
          isMobile
            ? 'flex flex-col gap-[22px] pt-2.5'
            : // Two columns until four cards fit with room for their CTAs.
              'grid grid-cols-1 items-stretch gap-4 pt-3 @min-[520px]/plans:grid-cols-2 @min-[880px]/plans:grid-cols-4',
        )}
      >
        {ordered.map((planKey) => {
          const isCurrent = currentPlan === planKey;
          const isPrimary =
            planKey === recommendedPlan &&
            !isCurrent &&
            (promoteTrial || !!focusFeatureLabel);
          const copy =
            planKey === WorkspacePlan.FREE
              ? plansPage.free
              : planKey === WorkspacePlan.PLUS
                ? plansPage.plus
                : planKey === WorkspacePlan.TEAMS
                  ? plansPage.teams
                  : plansPage.enterprise;
          const focusFeat =
            isPrimary &&
            focusFeatureLabel &&
            !copy.feats.includes(focusFeatureLabel)
              ? focusFeatureLabel
              : null;

          const price = resolvePrice(planKey, isYearly, locale, plansPage);
          const note = resolveNote(planKey, isYearly, days, plansPage, locale);
          const ctaLabel = resolveCta({
            planKey,
            isCurrent,
            promoteTrial,
            days,
            plansPage,
            planName: copy.name,
          });

          return (
            <article
              key={planKey}
              className={cn(
                'relative flex flex-col gap-[18px] rounded-[20px] bg-card p-5 sm:p-6',
                isPrimary
                  ? 'border-2 border-primary shadow-[0_12px_32px_rgba(42,85,229,0.14)] dark:shadow-[0_12px_32px_rgba(42,85,229,0.22)]'
                  : 'border border-border',
                selectedPlan === planKey &&
                  !isPrimary &&
                  'ring-1 ring-primary/40',
              )}
            >
              {isPrimary ? (
                <span className="absolute -top-3 left-5 rounded-full bg-primary-solid px-2.5 py-1 text-xs font-semibold text-primary-solid-foreground">
                  {plansPage.recommendedBadge}
                </span>
              ) : null}

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 text-[22px] font-semibold tracking-[-0.02em] text-foreground">
                    {copy.name}
                  </span>
                  {isCurrent ? (
                    <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                      {plansPage.currentBadge}
                    </span>
                  ) : null}
                  {(planKey === WorkspacePlan.PLUS ||
                    planKey === WorkspacePlan.TEAMS) &&
                  promoteTrial ? (
                    <span
                      className={cn(
                        'rounded-md px-2 py-0.5 text-[11px] font-semibold',
                        'bg-emerald-100 text-emerald-800',
                        'dark:bg-emerald-950 dark:text-emerald-300',
                      )}
                    >
                      {plansPage.trialBadge.replace('{days}', days)}
                    </span>
                  ) : null}
                </div>
                <p className="min-h-0 text-[13px] leading-snug text-muted-foreground sm:min-h-[38px]">
                  {copy.description}
                </p>
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-baseline gap-1.5">
                  <span
                    className={cn(
                      'font-semibold tracking-[-0.02em] text-foreground',
                      planKey === WorkspacePlan.ENTERPRISE
                        ? 'text-[30px]'
                        : 'text-[40px] leading-none',
                    )}
                  >
                    {price.amount}
                  </span>
                  {price.unit ? (
                    <span className="text-[13px] text-muted-foreground">
                      {price.unit}
                    </span>
                  ) : null}
                </div>
                <span className="text-xs text-muted-foreground">{note}</span>
              </div>

              <Button
                type="button"
                variant={
                  isCurrent ? 'ghost' : isPrimary ? 'primary' : 'outline'
                }
                className={cn(
                  'h-11 w-full font-semibold',
                  isCurrent &&
                    'cursor-default bg-muted text-muted-foreground hover:bg-muted',
                )}
                disabled={
                  !canManage ||
                  ctaBusy ||
                  isCurrent ||
                  (planKey === WorkspacePlan.FREE && promoteTrial)
                }
                // Plus and Teams share the trial label; the plan name keeps
                // the buttons apart for screen readers (DEV-540).
                aria-label={
                  ctaLabel.includes(copy.name)
                    ? undefined
                    : `${copy.name}: ${ctaLabel}`
                }
                onClick={() => {
                  onPlanChange(planKey);
                  if (planKey === WorkspacePlan.ENTERPRISE) {
                    onContactEnterprise();
                    return;
                  }
                  if (planKey === WorkspacePlan.FREE && isCurrent) return;
                  if (planKey === WorkspacePlan.FREE && promoteTrial) return;
                  onSelectPlanAction(planKey);
                }}
              >
                {ctaLabel}
              </Button>

              <div className="flex flex-col gap-2.5 border-t border-border/70 pt-4">
                <span className="text-xs font-semibold text-muted-foreground">
                  {copy.head}
                </span>
                <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                  {focusFeat ? (
                    <li className="flex gap-2.5 text-[13px] font-semibold leading-snug text-foreground">
                      <Check
                        className="mt-0.5 size-4 shrink-0 text-primary"
                        aria-hidden
                      />
                      <span>{focusFeat}</span>
                    </li>
                  ) : null}
                  {copy.feats.map((feat) => (
                    <li
                      key={feat}
                      className="flex gap-2.5 text-[13px] leading-snug text-foreground"
                    >
                      <Check
                        className="mt-0.5 size-4 shrink-0 text-primary"
                        aria-hidden
                      />
                      <span>{feat}</span>
                    </li>
                  ))}
                  {'locks' in copy
                    ? copy.locks.map((lock) => (
                        <li
                          key={lock}
                          className="flex gap-2.5 text-[13px] leading-snug text-muted-foreground"
                        >
                          <Lock
                            className="mt-0.5 size-[15px] shrink-0"
                            aria-hidden
                          />
                          <span>
                            <span className="sr-only">
                              {plansPage.notIncluded}:{' '}
                            </span>
                            {lock}
                          </span>
                        </li>
                      ))
                    : null}
                </ul>
              </div>
            </article>
          );
        })}
      </div>

      {!isMobile && !hideComparison ? (
        <BillingFeatureComparison highlightPlan={recommendedPlan} />
      ) : null}
    </div>
  );
}

function resolvePrice(
  planKey: PlanKey,
  isYearly: boolean,
  locale: string,
  plansPage: ReturnType<
    typeof useDictionary
  >['workspace']['billing']['plansPage'],
): { amount: string; unit: string } {
  if (planKey === WorkspacePlan.FREE) {
    return { amount: formatEurAmount(0, locale), unit: plansPage.foreverUnit };
  }
  if (planKey === WorkspacePlan.ENTERPRISE) {
    return { amount: plansPage.enterprise.price, unit: '' };
  }
  const pricing = PLAN_PRICING[planKey];
  const amount = isYearly
    ? yearlyMonthlyEquivalent(pricing.yearly)
    : pricing.monthly;
  return {
    amount: formatEurAmount(amount, locale),
    unit: plansPage.perSeatPerMonth,
  };
}

function resolveNote(
  planKey: PlanKey,
  isYearly: boolean,
  days: string,
  plansPage: ReturnType<
    typeof useDictionary
  >['workspace']['billing']['plansPage'],
  locale: string,
): string {
  if (planKey === WorkspacePlan.FREE) return plansPage.free.note;
  if (planKey === WorkspacePlan.ENTERPRISE) return plansPage.enterprise.note;
  if (planKey === WorkspacePlan.PLUS) {
    return isYearly
      ? plansPage.plus.noteYearly.replace(
          '{yearly}',
          formatEurAmount(PLAN_PRICING[WorkspacePlan.PLUS].yearly, locale),
        )
      : plansPage.plus.noteMonthly.replace('{days}', days);
  }
  return isYearly
    ? plansPage.teams.noteYearly.replace(
        '{yearly}',
        formatEurAmount(PLAN_PRICING[WorkspacePlan.TEAMS].yearly, locale),
      )
    : plansPage.teams.noteMonthly;
}

function resolveCta({
  planKey,
  isCurrent,
  promoteTrial,
  days,
  plansPage,
  planName,
}: {
  planKey: PlanKey;
  isCurrent: boolean;
  promoteTrial: boolean;
  days: string;
  plansPage: ReturnType<
    typeof useDictionary
  >['workspace']['billing']['plansPage'];
  planName: string;
}): string {
  if (isCurrent) return plansPage.ctaCurrent;
  if (planKey === WorkspacePlan.ENTERPRISE) return plansPage.ctaContact;
  if (planKey === WorkspacePlan.FREE) {
    return plansPage.ctaSelect.replace('{plan}', planName);
  }
  if (promoteTrial) return plansPage.ctaTrial.replace('{days}', days);
  return plansPage.ctaUpgrade.replace('{plan}', planName);
}
